import React, { useEffect, useState } from 'react';
import { Box, Link, Typography, keyframes } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TokenIcon from '@mui/icons-material/Token';
import { ACCOUNT_COLORS, accountCardSx } from '../../../components/account/accountPageStyles';
import { useClientTimeZone } from '../../../hooks/useClientTimeZone';
import { explorerAddressUrl, explorerTxUrl, shortenAddress } from '../config/cwyConfig';
import type { CwyTransferRequest } from '../services/walletService';
import {
  TRANSFER_PROGRESS_STEPS,
  formatProgressDateTime,
  getTransferProgressState,
} from '../utils/transferProgress';

interface CwyTransferProgressCardProps {
  request: CwyTransferRequest;
}

const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

const barPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 1, 0, 0.35); }
  50% { box-shadow: 0 0 14px 2px rgba(255, 1, 0, 0.45); }
`;

const barFlow = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const stepBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const stepPulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.65; }
  70% { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`;

const stepBounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

const iconSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const successGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(31, 169, 113, 0.3); }
  50% { box-shadow: 0 0 20px 4px rgba(31, 169, 113, 0.35); }
`;

const connectorFill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

function AnimatedProgressBar({
  percent,
  indeterminate,
  barColor,
  variant,
}: {
  percent: number;
  indeterminate: boolean;
  barColor: string;
  variant: 'active' | 'complete' | 'failed';
}) {
  const [animatedPercent, setAnimatedPercent] = useState(0);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setAnimatedPercent(percent));
    return () => window.cancelAnimationFrame(frame);
  }, [percent]);

  return (
    <Box
      sx={{
        position: 'relative',
        height: 8,
        borderRadius: 4,
        mb: 2.25,
        bgcolor: ACCOUNT_COLORS.surface3,
        overflow: 'hidden',
        ...(variant === 'complete' && {
          animation: `${successGlow} 2s ease-in-out infinite`,
        }),
      }}
    >
      {indeterminate ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(90deg, transparent, ${barColor}, #FF4040, ${barColor}, transparent)`,
            backgroundSize: '200% 100%',
            animation: `${barFlow} 1.6s linear infinite`,
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${animatedPercent}%`,
            borderRadius: 4,
            bgcolor: barColor,
            transition: 'width 0.85s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: variant === 'active' ? `${barPulse} 2s ease-in-out infinite` : undefined,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '45%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                animation: `${shimmer} 1.8s ease-in-out infinite`,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function StepDot({
  index,
  currentIndex,
  variant,
  failedStepIndex,
}: {
  index: number;
  currentIndex: number;
  variant: 'active' | 'complete' | 'failed';
  failedStepIndex?: number;
}) {
  const isFailed = variant === 'failed' && failedStepIndex === index;
  const isDone = variant === 'complete' ? index <= currentIndex : index < currentIndex;
  const isCurrent = variant !== 'failed' && index === currentIndex;
  const isUpcoming = !isDone && !isCurrent && !isFailed;

  let bg: string = ACCOUNT_COLORS.surface3;
  let color: string = ACCOUNT_COLORS.textDim;
  let border: string = `1px solid ${ACCOUNT_COLORS.borderSoft}`;

  if (isFailed) {
    bg = ACCOUNT_COLORS.dangerBg;
    color = ACCOUNT_COLORS.danger;
    border = `1px solid ${ACCOUNT_COLORS.dangerBorder}`;
  } else if (isDone) {
    bg = ACCOUNT_COLORS.successDim;
    color = ACCOUNT_COLORS.success;
    border = '1px solid rgba(55,203,139,.25)';
  } else if (isCurrent) {
    bg = ACCOUNT_COLORS.goldDim;
    color = ACCOUNT_COLORS.goldBright;
    border = `1px solid rgba(204, 27, 27, 0.35)`;
  }

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      {isCurrent && (
        <Box
          sx={{
            position: 'absolute',
            inset: -4,
            borderRadius: '50%',
            border: `2px solid ${ACCOUNT_COLORS.goldBright}`,
            animation: `${stepPulseRing} 1.8s ease-out infinite`,
          }}
        />
      )}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: bg,
          color,
          border,
          fontSize: 11,
          fontWeight: 700,
          fontFamily: ACCOUNT_COLORS.fontMono,
          opacity: isUpcoming ? 0.5 : 1,
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: isCurrent
            ? `${stepBounce} 1.6s ease-in-out infinite`
            : isDone
              ? undefined
              : undefined,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {isFailed ? (
          <ErrorOutlineIcon sx={{ fontSize: 15 }} />
        ) : isDone ? (
          <CheckCircleIcon sx={{ fontSize: 15 }} />
        ) : isCurrent ? (
          <HourglassTopIcon
            sx={{
              fontSize: 14,
              ...(variant === 'active' && currentIndex === 2
                ? { animation: `${iconSpin} 2.4s linear infinite` }
                : {}),
            }}
          />
        ) : (
          index + 1
        )}
      </Box>
    </Box>
  );
}

function StepConnector({ filled, active }: { filled: boolean; active: boolean }) {
  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        flex: 1,
        height: 2,
        mx: 0.5,
        mt: 1.75,
        bgcolor: ACCOUNT_COLORS.surface3,
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          transformOrigin: 'left center',
          bgcolor: filled ? ACCOUNT_COLORS.success : active ? ACCOUNT_COLORS.goldBright : 'transparent',
          transform: filled || active ? 'scaleX(1)' : 'scaleX(0)',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: active && !filled ? `${connectorFill} 1.2s ease-in-out infinite alternate` : undefined,
        }}
      />
    </Box>
  );
}

export const CwyTransferProgressCard: React.FC<CwyTransferProgressCardProps> = ({ request }) => {
  const timeZone = useClientTimeZone();
  const progress = getTransferProgressState(request);

  const barColor =
    progress.variant === 'failed'
      ? ACCOUNT_COLORS.danger
      : progress.variant === 'complete'
        ? ACCOUNT_COLORS.success
        : ACCOUNT_COLORS.goldBright;

  return (
    <Box
      sx={{
        ...accountCardSx,
        animation: `${cardEnter} 0.45s cubic-bezier(0.4, 0, 0.2, 1)`,
        borderColor:
          progress.variant === 'failed'
            ? ACCOUNT_COLORS.dangerBorder
            : progress.variant === 'complete'
              ? 'rgba(55,203,139,.35)'
              : 'rgba(204, 27, 27, 0.25)',
        background:
          progress.variant === 'active'
            ? `linear-gradient(135deg, ${ACCOUNT_COLORS.surface} 0%, rgba(255, 1, 0, 0.03) 100%)`
            : ACCOUNT_COLORS.surface,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: ACCOUNT_COLORS.goldDim,
            color: ACCOUNT_COLORS.goldBright,
            flexShrink: 0,
            animation:
              progress.indeterminate
                ? `${stepBounce} 1.4s ease-in-out infinite`
                : progress.variant === 'complete'
                  ? `${successGlow} 2s ease-in-out infinite`
                  : undefined,
          }}
        >
          <TokenIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: ACCOUNT_COLORS.textDim,
              animation: progress.variant === 'active' ? `${stepBlink} 2.2s ease-in-out infinite` : undefined,
            }}
          >
            CWY transfer in progress
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: ACCOUNT_COLORS.text, mt: 0.25 }}>
            {progress.headline}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: ACCOUNT_COLORS.textLo, mt: 0.5, lineHeight: 1.45 }}>
            {progress.subline}
          </Typography>
        </Box>
      </Box>

      <AnimatedProgressBar
        percent={progress.percent}
        indeterminate={progress.indeterminate}
        barColor={barColor}
        variant={progress.variant}
      />

      <Box sx={{ display: { xs: 'grid', sm: 'flex' }, gridTemplateColumns: { xs: '1fr 1fr' }, gap: 1.5, alignItems: 'flex-start' }}>
        {TRANSFER_PROGRESS_STEPS.map((step, index) => {
          const isCurrent = progress.variant !== 'failed' && index === progress.stepIndex;
          const isFailed = progress.variant === 'failed' && progress.failedStepIndex === index;
          const isDone =
            progress.variant === 'complete'
              ? index <= progress.stepIndex
              : index < progress.stepIndex;
          const isPastCurrent = isDone && !isCurrent;

          return (
            <React.Fragment key={step.key}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  p: 1,
                  borderRadius: ACCOUNT_COLORS.radiusSm,
                  transition: 'background-color 0.3s ease',
                  bgcolor: isCurrent ? 'rgba(255, 1, 0, 0.05)' : 'transparent',
                  border: isCurrent ? '1px solid rgba(204, 27, 27, 0.15)' : '1px solid transparent',
                }}
              >
                <StepDot
                  index={index}
                  currentIndex={progress.stepIndex}
                  variant={progress.variant}
                  failedStepIndex={progress.failedStepIndex}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: isCurrent || isFailed ? 700 : 600,
                      color: isFailed
                        ? ACCOUNT_COLORS.danger
                        : isCurrent
                          ? ACCOUNT_COLORS.goldBright
                          : isDone
                            ? ACCOUNT_COLORS.text
                            : ACCOUNT_COLORS.textDim,
                      animation: isCurrent ? `${stepBlink} 1.4s ease-in-out infinite` : undefined,
                    }}
                  >
                    {step.label}
                    {isCurrent && progress.variant === 'active' && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: ACCOUNT_COLORS.goldBright,
                          ml: 0.75,
                          verticalAlign: 'middle',
                          animation: `${stepBlink} 1s ease-in-out infinite`,
                        }}
                      />
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: isCurrent ? ACCOUNT_COLORS.textLo : ACCOUNT_COLORS.textDim,
                      mt: 0.25,
                      lineHeight: 1.35,
                      animation: isCurrent ? `${stepBlink} 2s ease-in-out infinite` : undefined,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
              {index < TRANSFER_PROGRESS_STEPS.length - 1 && (
                <StepConnector filled={isPastCurrent} active={isCurrent} />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mt: 2.25,
          pt: 2,
          borderTop: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: ACCOUNT_COLORS.textDim }}>
            Wallet
          </Typography>
          <Link
            href={explorerAddressUrl(request.wallet_address)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontFamily: ACCOUNT_COLORS.fontMono,
              fontSize: 11.5,
              color: ACCOUNT_COLORS.text,
              textDecoration: 'none',
              '&:hover': { color: ACCOUNT_COLORS.goldBright },
            }}
          >
            {shortenAddress(request.wallet_address, 8, 6)}
          </Link>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: ACCOUNT_COLORS.textDim }}>
            Requested
          </Typography>
          <Typography sx={{ fontFamily: ACCOUNT_COLORS.fontMono, fontSize: 11.5, color: ACCOUNT_COLORS.text }}>
            {formatProgressDateTime(request.createdAt, timeZone) || '—'}
          </Typography>
        </Box>
        {request.reviewed_at && (
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: ACCOUNT_COLORS.textDim }}>
              Reviewed
            </Typography>
            <Typography sx={{ fontFamily: ACCOUNT_COLORS.fontMono, fontSize: 11.5, color: ACCOUNT_COLORS.text }}>
              {formatProgressDateTime(request.reviewed_at, timeZone)}
            </Typography>
          </Box>
        )}
        {request.tx_confirmed_at && (
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: ACCOUNT_COLORS.textDim }}>
              Confirmed
            </Typography>
            <Typography sx={{ fontFamily: ACCOUNT_COLORS.fontMono, fontSize: 11.5, color: ACCOUNT_COLORS.text }}>
              {formatProgressDateTime(request.tx_confirmed_at, timeZone)}
            </Typography>
          </Box>
        )}
      </Box>

      {request.tx_signature && (
        <Link
          href={explorerTxUrl(request.tx_signature)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1.75,
            fontSize: 12.5,
            fontWeight: 600,
            color: ACCOUNT_COLORS.goldBright,
            textDecoration: 'none',
            '&:hover': { color: ACCOUNT_COLORS.gold },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 15 }} />
          View transaction on Solana Explorer
        </Link>
      )}
    </Box>
  );
};
